/**
 * AdminAffairsHubPage — Issue #365.
 *
 * Lightweight entry page that lets the user navigate to the individual
 * administrative-affairs surfaces (meeting rooms, bookings, vehicle / seal
 * requests, announcements). Each card respects the relevant permission gate.
 */
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_AFFAIRS_PERMISSIONS,
  canSeeAdminAffairsMenu,
  hasPermission,
} from '@/lib/adminAffairsPermissions';

interface HubCard {
  readonly href: string;
  readonly title: string;
  readonly description: string;
  readonly testId: string;
  readonly visible: (perms: readonly string[]) => boolean;
}

const HUB_CARDS: ReadonlyArray<HubCard> = [
  {
    href: '/admin/affairs/meeting-rooms',
    title: '会议室主数据',
    description: '维护会议室名称、容量、设备和可用状态，归档失效会议室。',
    testId: 'admin-affairs-hub-meeting-rooms',
    visible: (p) => hasPermission(p, ADMIN_AFFAIRS_PERMISSIONS.READ),
  },
  {
    href: '/admin/affairs/bookings',
    title: '会议室预订',
    description:
      '查看 / 创建会议室预订，行政审批确认或拒绝；时间冲突在后端检查。',
    testId: 'admin-affairs-hub-bookings',
    visible: (p) => hasPermission(p, ADMIN_AFFAIRS_PERMISSIONS.READ),
  },
  {
    href: '/admin/affairs/vehicles',
    title: '用车申请',
    description: '员工发起用车申请，行政按审批流确认；不替代财务报销。',
    testId: 'admin-affairs-hub-vehicles',
    visible: (p) => hasPermission(p, ADMIN_AFFAIRS_PERMISSIONS.READ),
  },
  {
    href: '/admin/affairs/seals',
    title: '用印申请',
    description: '按用印类型走审批 / 拒绝 / 用印完成；附件仅承载 file_id。',
    testId: 'admin-affairs-hub-seals',
    visible: (p) => hasPermission(p, ADMIN_AFFAIRS_PERMISSIONS.READ),
  },
  {
    href: '/admin/affairs/announcements',
    title: '行政公告',
    description: '发布行政公告，跟踪阅读 / 确认回执；管理员可撤回或归档。',
    testId: 'admin-affairs-hub-announcements',
    visible: (p) => hasPermission(p, ADMIN_AFFAIRS_PERMISSIONS.ANNOUNCEMENT_READ),
  },
];

export default function AdminAffairsHubPage() {
  const { permissions } = useAuth();
  if (!canSeeAdminAffairsMenu(permissions)) {
    return <AccessDenied resource="行政事务" />;
  }
  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">行政事务</h1>
        <p className="text-sm text-slate-500">
          会议室预订、用车 / 用印审批与行政公告统一入口。审批不替代财务付款与报销，
          仅承载行政事务申请与回执状态。
        </p>
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {HUB_CARDS.filter((c) => c.visible(permissions)).map((card) => (
          <Link key={card.href} href={card.href} data-testid={card.testId}>
            <Card className="cursor-pointer p-5 transition hover:bg-muted">
              <h2 className="text-base font-medium">{card.title}</h2>
              <p className="mt-2 text-sm text-slate-500">{card.description}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
